/**
 * The HTTP client.
 *
 * No token is ever handled here, or anywhere else in the browser. Sessions are
 * HttpOnly cookies, so this only ever sets `credentials: 'include'`. That
 * removes the whole token-theft-by-XSS class, and it is the one part of the
 * reference system's security design worth copying verbatim.
 *
 * The refresh algorithm below is also theirs, and it is worth porting almost
 * exactly because every clause in it is load-bearing:
 *
 *   - exactly one refresh is in flight at a time, so ten concurrent 401s cause
 *     one refresh and not ten (the second one would invalidate the first);
 *   - a sticky "session is dead" flag, so a failed refresh does not send every
 *     subsequent request through the same doomed retry;
 *   - the session-expired callback fires once, not once per queued request;
 *   - login and refresh themselves are exempt from retry, or a bad password
 *     becomes an infinite loop.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  baseUrl: string;
  /** Called once when the session is gone for good. */
  onSessionExpired?: () => void;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set for endpoints that must never trigger a refresh-and-retry. */
  noRetry?: boolean;
}

export class ApiClient {
  private refreshInFlight: Promise<boolean> | null = null;
  private sessionDead = false;
  private expiredNotified = false;

  constructor(private readonly opts: ApiOptions) {}

  get baseUrl(): string {
    return this.opts.baseUrl;
  }

  /** Call after a successful login so a previously dead client works again. */
  resetSession(): void {
    this.sessionDead = false;
    this.expiredNotified = false;
    this.refreshInFlight = null;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const res = await this.send(path, options);

    if (res.status === 401 && !options.noRetry && !this.sessionDead) {
      const refreshed = await this.refresh();
      if (refreshed) {
        const retry = await this.send(path, options);
        return this.parse<T>(retry);
      }
      this.notifyExpired();
    }

    return this.parse<T>(res);
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    return fetch(`${this.opts.baseUrl}${path}`, {
      method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
      headers,
      credentials: 'include',
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const data = text ? safeJson(text) : null;

    if (!res.ok) {
      const err = (data as { error?: { code?: string; message?: string; details?: unknown } })
        ?.error;
      throw new ApiError(
        res.status,
        err?.message ?? (data as { message?: string })?.message ?? res.statusText,
        err?.code,
        err?.details,
      );
    }
    return data as T;
  }

  /** One refresh at a time, no matter how many callers are waiting on it. */
  private refresh(): Promise<boolean> {
    if (this.sessionDead) return Promise.resolve(false);
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const res = await this.send('/auth/refresh', { method: 'POST', noRetry: true });
        if (res.ok) return true;
        this.sessionDead = true;
        return false;
      } catch {
        // A network failure is not a dead session — do not latch the flag, or a
        // brief offline moment logs the agent out mid-conversation.
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private notifyExpired(): void {
    if (this.expiredNotified) return;
    this.expiredNotified = true;
    this.opts.onSessionExpired?.();
  }

  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', signal });
  }

  post<T>(path: string, body?: unknown, extra: Partial<RequestOptions> = {}): Promise<T> {
    return this.request<T>(path, { method: 'POST', body, ...extra });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text.slice(0, 300) };
  }
}
